import { throwIfAborted } from './fetchCancellation';

/** Items core v1.132.5 marks its xdomain transport frame with this class and reuses it by lookup. */
export const XDOMAIN_FRAME_CLASS = 'x-origin-frame';
const ITEMS_ORIGIN = 'https://items.learnosity.com';
const XDOMAIN_SOURCE = /^https:\/\/items\.learnosity\.com\/[^/?#]+\/xdomain(\?chromeApp)?$/;

export interface LearnosityXdomainFrameState {
  announcedAt?: number;
  carriedRequest: boolean;
}

export interface LearnosityXdomainObserver {
  stateFor(source: unknown): LearnosityXdomainFrameState | undefined;
}

export class LearnosityTransportError extends Error {
  constructor(readonly kind: 'reload_unsafe') {
    super('Learnosity Items SDK reload blocked by a transport that never announced readiness');
    this.name = 'LearnosityTransportError';
  }
}

/** Mirrors public/learnosity-xdomain-observer.js for documents loaded without that script. */
function installObserver(): LearnosityXdomainObserver {
  const states = new WeakMap<object, LearnosityXdomainFrameState>();
  window.addEventListener('message', event => {
    if (event.origin !== ITEMS_ORIGIN || !event.source) return;
    let state = states.get(event.source);
    if (state?.carriedRequest) return;
    let data: unknown = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;
    const message = data as { ready?: unknown; id?: unknown; status?: unknown };
    const announced = !!message.ready;
    const carried = message.id !== undefined && typeof message.status === 'number';
    if (!announced && !carried) return;
    if (!state) {
      state = { announcedAt: undefined, carriedRequest: false };
      states.set(event.source, state);
    }
    if (announced && state.announcedAt === undefined) state.announcedAt = performance.now();
    if (carried) state.carriedRequest = true;
  });
  const observer: LearnosityXdomainObserver = Object.freeze({
    stateFor: (source: unknown) => {
      const state = source && typeof source === 'object' ? states.get(source) : undefined;
      return state && { announcedAt: state.announcedAt, carriedRequest: state.carriedRequest };
    },
  });
  Object.defineProperty(window, '__learnosityXdomain', { configurable: true, value: observer });
  return observer;
}

export function learnosityXdomainObserver(): LearnosityXdomainObserver {
  return window.__learnosityXdomain ?? installObserver();
}

/** The frame Items core v1.132.5 reuses: the first classed xdomain frame in document order. */
export function selectedXdomainFrame(): HTMLIFrameElement | null {
  for (const frame of document.querySelectorAll<HTMLIFrameElement>(`iframe.${XDOMAIN_FRAME_CLASS}`)) {
    if (XDOMAIN_SOURCE.test(frame.src)) return frame;
  }
  return null;
}

function frameState(frame: HTMLIFrameElement) {
  return learnosityXdomainObserver().stateFor(frame.contentWindow);
}

/** A frame that carried a request must have announced, even if observation began later. */
export function hasXdomainFrameAnnounced(frame: HTMLIFrameElement): boolean {
  const state = frameState(frame);
  return !!state && (state.announcedAt !== undefined || state.carriedRequest);
}

export function hasXdomainFrameCarriedRequest(frame: HTMLIFrameElement): boolean {
  return !!frameState(frame)?.carriedRequest;
}

/**
 * Hide a spent frame from Items core's lookup so its next startup creates a fresh one. The frame
 * stays attached, so a core that already adopted it keeps a working transport.
 */
export function retireXdomainFrame(frame: HTMLIFrameElement): void {
  frame.classList.remove(XDOMAIN_FRAME_CLASS);
  frame.dataset.lrnRetired = 'true';
}

export function waitForXdomainAnnouncement(
  frame: HTMLIFrameElement,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<boolean> {
  throwIfAborted(signal);
  if (hasXdomainFrameAnnounced(frame)) return Promise.resolve(true);
  return new Promise((resolve, reject) => {
    const finish = (settle: () => void) => {
      clearTimeout(timer);
      window.removeEventListener('message', check);
      signal.removeEventListener('abort', abort);
      settle();
    };
    // The observer listens first, so its state is current by the time this listener runs.
    const check = () => {
      if (hasXdomainFrameAnnounced(frame)) finish(() => resolve(true));
    };
    const abort = () => finish(() => reject(new DOMException('Request cancelled', 'AbortError')));
    const timer = setTimeout(() => finish(() => resolve(false)), timeoutMs);
    window.addEventListener('message', check);
    signal.addEventListener('abort', abort, { once: true });
  });
}
